import React from 'react';

import { useIntl, type MessageDescriptor } from 'react-intl';

import { HUB_ROUTES } from '../constants';
import { useHubBreadcrumbs } from '../hub-breadcrumbs';
import { staticHubBreadcrumbMessages } from './messages';

interface StaticHubBreadcrumbRoute {
	id: string;
	message: MessageDescriptor;
	path: string;
}

export const STATIC_HUB_BREADCRUMB_ROUTES: Readonly<Record<string, StaticHubBreadcrumbRoute>> = {
	[HUB_ROUTES.JSM_REQUESTS_LIST.name]: {
		id: 'requests',
		message: staticHubBreadcrumbMessages.requests,
		path: HUB_ROUTES.JSM_REQUESTS_LIST.path,
	},
	[HUB_ROUTES.JSM_APPROVALS.name]: {
		id: 'approvals',
		message: staticHubBreadcrumbMessages.approvals,
		path: HUB_ROUTES.JSM_APPROVALS.path,
	},
	[HUB_ROUTES.USER_PROFILE.name]: {
		id: 'profile',
		message: staticHubBreadcrumbMessages.profile,
		path: HUB_ROUTES.USER_PROFILE.path,
	},
};

export const getStaticHubBreadcrumbRoute = (
	routeName: string,
): StaticHubBreadcrumbRoute | undefined => STATIC_HUB_BREADCRUMB_ROUTES[routeName];

export const StaticHubBreadcrumbs = ({
	children,
	routeName,
}: {
	children: React.ReactNode;
	routeName: string;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const route = getStaticHubBreadcrumbRoute(routeName);

	useHubBreadcrumbs({
		currentItem: route
			? {
					href: route.path,
					id: route.id,
					text: formatMessage(route.message),
				}
			: undefined,
	});

	return <>{children}</>;
};

export const withStaticHubBreadcrumbs = <Props extends object>(
	Component: React.ComponentType<Props>,
	routeName: string,
): React.ComponentType<Props> => {
	const ComponentWithStaticHubBreadcrumbs = (props: Props) => (
		<StaticHubBreadcrumbs routeName={routeName}>
			<Component {...props} />
		</StaticHubBreadcrumbs>
	);

	ComponentWithStaticHubBreadcrumbs.displayName = `withStaticHubBreadcrumbs(${
		Component.displayName ?? Component.name ?? 'Component'
	})`;

	return ComponentWithStaticHubBreadcrumbs;
};
